import React, {useState, useEffect, useCallback, useRef} from 'react';
import {Sparkles, ChevronDown, ChevronUp, Clock, Loader2} from 'lucide-react';
import {PhoneHQButton} from 'ch3-ui-lib';
import {MatrixEventEvent, RoomEvent} from 'matrix-js-sdk';
import type {MatrixEvent, Room} from 'matrix-js-sdk';
import {matrixService} from '../../services/matrixService';
import {isSummaryMessageContent} from '../../utilis/chatUtils';
import {isBotUser} from '../../utils/aiBotUtils';
import {aiStatusText, summaryVisibleTo} from '../../utils/aiRequestProtocol';
import type {SummaryPeriod} from '../../utils/aiRequestProtocol';
import {useAiRequest} from './useAiRequest';
import {SummaryResult} from './SummaryResult';
import {parseSummaryPresentation} from './summaryPresentation';
import type {SummaryPresentation} from './summaryPresentation';

interface SummaryEntry {
    eventId: string;
    requestEventId: string;
    summary: SummaryPresentation;
    timestamp: number;
}

interface ChatSummaryPanelProps {
    roomId: string;
    currentUserId: string;
    isAiBotInRoom: boolean;
}

const TIME_RANGES = [
    {label: '24h', value: '24h'},
    {label: '3d', value: '3d'},
    {label: '1w', value: '1w'},
    {label: '2w', value: '2w'},
    {label: '3w', value: '3w'},
    {label: '1m', value: '1m'},
] as const;

const ChatSummaryPanel: React.FC<ChatSummaryPanelProps> = ({roomId, currentUserId, isAiBotInRoom}) => {
    const [selectedRange, setSelectedRange] = useState<SummaryPeriod>('24h');
    const request = useAiRequest({roomId, currentUserId, kind: 'summary', enabled: isAiBotInRoom});
    const isLoading = request.pending || request.sending;
    const [summaries, setSummaries] = useState<SummaryEntry[]>([]);
    const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);
    const pendingRequestEventId = request.requestEventId;
    const error = request.transportError ?? (request.status?.state === 'failed' ? aiStatusText(request.status) : undefined);

    // Track which event IDs existed when the panel first mounted (pre-session summaries)
    const initialEventIdsRef = useRef<Set<string> | null>(null);
    // Track event IDs generated in this session
    const sessionGeneratedIdsRef = useRef<Set<string>>(new Set());

    const scanForSummaries = useCallback(() => {
        const client = matrixService.getClient();
        if (!client) return;

        const room = client.getRoom(roomId);
        if (!room) return;

        const timeline = room.getLiveTimeline();
        const events = timeline.getEvents();

        const found: SummaryEntry[] = [];

        // Build an event-by-id lookup once so the per-event filtering below is
        // O(N) overall instead of O(N^2) from repeated events.find() calls.
        const eventsById = new Map<string, MatrixEvent>();
        for (const e of events) {
            const id = e.getId();
            if (id) {
                eventsById.set(id, e);
            }
        }

        for (const event of events) {
            const content = event.getContent();
            if (event.isRedacted() || !isBotUser(event.getSender() ?? '', currentUserId)
                || !isSummaryMessageContent(content)) continue;

            const summary = parseSummaryPresentation(content);
            const eventId = event.getId();
            if (!summary || !eventId) continue;
            const {data, requestEventId} = summary;

            if (!summaryVisibleTo({
                request: requestEventId ? eventsById.get(requestEventId) : undefined,
                fallbackRequesterId: data.requester_id,
                currentUserId,
            })) continue;

            found.push({
                eventId,
                requestEventId: requestEventId || '',
                summary,
                timestamp: event.getTs(),
            });
        }

        found.sort((a, b) => b.timestamp - a.timestamp);

        // On first scan, capture existing event IDs as "pre-session"
        if (initialEventIdsRef.current === null) {
            initialEventIdsRef.current = new Set(found.map(s => s.eventId));
        }

        setSummaries(found);

        // Track results received for the active request in this panel session.
        if (pendingRequestEventId) {
            const matchingResult = found.find(s => s.requestEventId === pendingRequestEventId);
            if (matchingResult && !initialEventIdsRef.current?.has(matchingResult.eventId)) {
                // Mark this as session-generated
                sessionGeneratedIdsRef.current.add(matchingResult.eventId);
            }
        }
    }, [roomId, currentUserId, pendingRequestEventId]);

    useEffect(() => {
        initialEventIdsRef.current = null;
        sessionGeneratedIdsRef.current.clear();
        setExpandedHistoryId(null);
    }, [roomId, currentUserId]);

    useEffect(() => {
        scanForSummaries();

        const client = matrixService.getClient();
        if (!client) return;

        const handleTimeline = (event: MatrixEvent, room?: Room) => {
            if (room?.roomId !== roomId) return;
            const content = event.getContent();
            if (isSummaryMessageContent(content)) {
                scanForSummaries();
            }
        };

        // Also listen for decrypted events in encrypted rooms
        const handleDecrypted = (event: MatrixEvent) => {
            if (event.getRoomId() !== roomId) return;
            const content = event.getContent();
            if (isSummaryMessageContent(content)) {
                scanForSummaries();
            }
        };

        const handleNewMessage = ({event, room}: {event: MatrixEvent; room: Room}) => {
            if (room?.roomId !== roomId) return;
            const content = event.getContent();
            if (isSummaryMessageContent(content)) {
                scanForSummaries();
            }
        };

        const handleRedaction = (_event: MatrixEvent, room: Room) => {
            if (room.roomId === roomId) scanForSummaries();
        };
        client.on(RoomEvent.Redaction, handleRedaction);
        client.on(RoomEvent.Timeline, handleTimeline);
        client.on(MatrixEventEvent.Decrypted, handleDecrypted);
        matrixService.on('newMessage', handleNewMessage);
        return () => {
            client.off(RoomEvent.Redaction, handleRedaction);
            client.off(RoomEvent.Timeline, handleTimeline);
            client.off(MatrixEventEvent.Decrypted, handleDecrypted);
            matrixService.off('newMessage', handleNewMessage);
        };
    }, [roomId, scanForSummaries]);

    const startRequest = request.start;
    const handleGenerate = useCallback(async () => {
        await startRequest({kind: 'summary', period: selectedRange});
    }, [startRequest, selectedRange]);

    // Split summaries: session-generated go to "latest", pre-existing go to "previous"
    const sessionSummaries = summaries.filter(s => sessionGeneratedIdsRef.current.has(s.eventId));
    const previousSummaries = summaries.filter(s => !sessionGeneratedIdsRef.current.has(s.eventId));
    const latestSessionSummary = sessionSummaries[0] || null;
    const olderSessionSummaries = sessionSummaries.slice(1);
    const allPrevious = [...olderSessionSummaries, ...previousSummaries];

    return (
        <div className="p-4 border-b border-line">
            <div className="flex items-center gap-2 mb-3">
                <Sparkles size={14} className="text-brand-ink"/>
                <h5 className="font-semibold text-ink text-sm">AI Summary</h5>
            </div>

            {/* Time range selector */}
            <div className="flex flex-wrap justify-center gap-1 mb-3">
                {TIME_RANGES.map(({label, value}) => (
                    <button
                        key={value}
                        onClick={() => setSelectedRange(value)}
                        className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                            selectedRange === value
                                ? 'bg-brand-tint text-brand-ink font-medium'
                                : 'bg-surface-muted text-ink-muted-strong hover:bg-line'
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Generate button */}
            <div className="w-full">
            <PhoneHQButton
                onClick={handleGenerate}
                disabled={isLoading || !isAiBotInRoom}
                className="flex justify-center box-border w-full"
            >
                {isLoading ? (
                    <>
                        <Loader2 color={'white'} size={16} className="animate-spin"/>
                        Generating summary...
                    </>
                ) : (
                    <>
                        <Sparkles color={'white'} size={16}/>
                        Generate Summary
                    </>
                )}
            </PhoneHQButton>
            </div>

            {!isAiBotInRoom && (
                <p className="mt-1.5 text-xs text-ink-muted text-center">
                    AI bot is not in this room. Use the bot icon in the header to invite it.
                </p>
            )}

            {/* Error */}
            {error && (
                <div className="mt-2 text-xs text-red-600 bg-red-50 rounded-md p-2">
                    {error}
                </div>
            )}

            {request.status && request.status.state !== 'failed' && request.status.state !== 'published' && (
                <p className="mt-2 text-xs text-ink-muted" role="status">{aiStatusText(request.status)}</p>
            )}
            {request.status?.retryAllowed && !request.resultReceived && (
                <button type="button" disabled={request.sending || !isAiBotInRoom} onClick={() => { void request.retry(); }}
                    className="mt-2 text-xs text-brand-ink underline">Retry original summary</button>
            )}
            {request.transportError && (
                <button type="button" disabled={request.sending || !isAiBotInRoom} onClick={() => { void request.recover(); }}
                    className="mt-2 ml-2 text-xs text-brand-ink underline">Check existing request</button>
            )}

            {/* Current session summary (only summaries generated in this session) */}
            {latestSessionSummary && (
                <div className="mt-3 p-3 bg-brand-tint-subtle rounded-lg border border-indigo-100">
                    <SummaryResult summary={latestSessionSummary.summary}/>
                </div>
            )}

            {/* Previous summaries (pre-existing + older session ones) */}
            {allPrevious.length > 0 && (
                <div className="mt-3">
                    <p className="text-xs font-medium text-ink-muted mb-2 flex items-center gap-1">
                        <Clock size={12}/>
                        Previous summaries
                    </p>
                    <div className="space-y-1">
                        {allPrevious.slice(0, 5).map((entry) => (
                            <div key={entry.eventId} className="border border-line rounded-md">
                                <button
                                    onClick={() => setExpandedHistoryId(expandedHistoryId === entry.eventId ? null : entry.eventId)}
                                    className="box-border w-full px-2.5 py-1.5 text-xs text-left flex items-center justify-between hover:bg-canvas transition-colors"
                                >
                                    <span className="text-ink-muted-strong truncate">
                                        {new Date(entry.timestamp).toLocaleDateString()} - {entry.summary.data.message_count} messages
                                    </span>
                                    {expandedHistoryId === entry.eventId ?
                                        <ChevronUp size={12} className="text-ink-muted flex-shrink-0"/> :
                                        <ChevronDown size={12} className="text-ink-muted flex-shrink-0"/>
                                    }
                                </button>
                                {expandedHistoryId === entry.eventId && (
                                    <div className="px-2.5 pb-2">
                                        <SummaryResult summary={entry.summary} density="compact"/>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatSummaryPanel;

